import React, { useEffect, useState } from "react";
import { Link, useLocation } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import jsPDF from "jspdf";
import "jspdf-autotable";
import { userRequest } from '../../../utils/requestMethods';
import { BackButton, CustomPagination, SkeletonTable, StatusBadge } from '../../../components';
import { useStateContext } from '../../../contexts/ContextProvider';
import EditResultDetailsModal from "../../../components/teacherModule/editResult/EditResultDetailsModal";
import AddCommentModal from "../../../components/teacherModule/results/AddCommentModal";
import { generateSchoolSessions } from '../../../utils/schoolYearUtils';
import { extractLogoColors, getDefaultColorPalette, lightenColor, loadImageAsDataUrl } from '../../../utils/colorExtraction';
import { drawFittedTable } from '../../../utils/resultPdfLayout';
import { FiDownload, FiEdit3, FiFileText, FiMessageSquare, FiUser } from "react-icons/fi";
import emmymaxLogo from "../../../data/logo/logo.png";

const customStyles = {
    headRow: {
        style: {
            backgroundColor: 'rgb(248, 250, 252)',
            fontWeight: "bold",
        },
    },
};

const getStudentFullName = (child, fallbackMiddlename) => {
    if (!child) return "Student";
    return [child.lastname, child.firstname, child.middlename || fallbackMiddlename]
        .filter(Boolean)
        .join(" ") || "Student";
};

const ViewStudentResult = ({ history }) => {
    const location = useLocation();
    const studentId = location.pathname.split('/')[4];
    const student = location.state?.student;
    const classId = student.class_info._id;
    console.log(student);
    const { user, school } = useStateContext();
    const schoolTerm = school?.settings?.current_term;
    const schoolSession = school?.settings?.current_session;
    const terminology = school?.settings?.term_terminology;
    const [results, setResults] = useState([]);
    const [fullResults, setFullResults] = useState({});
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
    const [attendanceSummary, setAttendanceSummary] = useState(null);
    // The result endpoint's embedded `child` object doesn't include middlename —
    // fetch it separately from the student record so exported names can include it.
    const [studentMiddlename, setStudentMiddlename] = useState('');

    // Term and Session filter states
    const [termDropdown, setTermDropdown] = useState(false);
    const [sessionDropdown, setSessionDropdown] = useState(false);
    const [selectedTerm, setSelectedTerm] = useState(() => {
        const saved = JSON.parse(localStorage.getItem('viewStudentResultSelectedTerm'));
        return saved || { id: schoolTerm || '1', name: `Term ${schoolTerm || '1'}` };
    });
    const [selectedSession, setSelectedSession] = useState(() => {
        const saved = JSON.parse(localStorage.getItem('viewStudentResultSelectedSession'));
        return saved || { id: schoolSession || '2024/2025', name: schoolSession || '2024/2025' };
    });

    // Generate school sessions using utility function
    const schoolSessions = generateSchoolSessions(2020, 10); // Generate sessions from 2020 to 2030

    // Get assessment system based on subject's level of education
    const getAssessmentSystemForSubject = (subject) => {
        if (!subject?.class?.level_of_education || !school?.settings?.assessment?.assessment_system) {
            // No assessment system available, fallback to default assessment
            return { ca: { cas: [] }, exam: { max_score: 100 } };
        }

        const levelOfEducation = subject.class.level_of_education;

        // Find the assessment system for this level of education
        const assessmentConfig = school.settings.assessment.assessment_system.find(
            (config) => config.level_of_education === levelOfEducation
        );

        // If found, use it; otherwise, use the default one
        if (assessmentConfig) {
            return assessmentConfig;
        }

        // Fallback to default assessment system
        const defaultConfig = school.settings.assessment.assessment_system.find(
            (config) => config.is_default === true
        );

        return defaultConfig || { ca: { cas: [] }, exam: { max_score: 100 } };
    };

    // Get assessment system based on class level of education
    const getAssessmentSystemForClass = (classObj) => {
        if (!classObj?.level_of_education || !school?.settings?.assessment?.assessment_system) {
            // No assessment system available, fallback to default CA structure
            return { ca: { cas: [] }, exam: { max_score: 100 } };
        }

        const levelOfEducation = classObj.level_of_education;

        // Find the assessment system for this level of education
        const assessmentConfig = school.settings.assessment.assessment_system.find(
            (config) => config.level_of_education === levelOfEducation
        );

        // If found, use it; otherwise, use the default one
        if (assessmentConfig) {
            return assessmentConfig;
        }

        // Fallback to default assessment system
        const defaultConfig = school.settings.assessment.assessment_system.find(
            (config) => config.is_default === true
        );

        return defaultConfig || { ca: { cas: [] }, exam: { max_score: 100 } };
    };

    // Calculate grade based on score and grading system
    const calculateGrade = (score, gradingSystem) => {
        if (!score || score === 0 || !gradingSystem || gradingSystem.length === 0) {
            return '-';
        }

        for (const gradeConfig of gradingSystem) {
            if (score >= gradeConfig.min_score && score <= gradeConfig.max_score) {
                return gradeConfig.grade;
            }
        }

        return '-'; // Fallback if no match found
    };

    // Get the class object from fullResults or use a fallback
    const getClassFromResult = () => {
        // Try to get class from first result
        if (fullResults?.results?.[0]?.class) {
            return fullResults.results[0].class;
        }
        // If not available, return null and we'll use default
        return null;
    };

    // Get current assessment system based on student's class
    const classObj = getClassFromResult();
    const currentAssessmentSystem = classObj
        ? getAssessmentSystemForClass(classObj)
        : (school?.settings?.assessment?.assessment_system?.find(config => config.is_default === true) || { ca: { cas: [] }, exam: { max_score: 100 } });

    const caSettings = currentAssessmentSystem?.ca?.cas || [];
    const examMaxScore = currentAssessmentSystem?.exam?.max_score || 100;

    // Get grading system from API response or fallback to school settings
    const gradingSystem = (fullResults.grading_system && Array.isArray(fullResults.grading_system) && fullResults.grading_system.length > 0)
        ? fullResults.grading_system
        : ((school?.settings?.grading?.grading_system || []).find(gs => gs.level_of_education === "primary")?.grades || []);

    const toggleResultModal = (subject = null) => {
        setSelectedSubject(subject);
        setIsModalOpen(!isModalOpen);
    };

    const toggleCommentModal = () => {
        setIsCommentModalOpen(!isCommentModalOpen);
    };

    const openScoreEntry = () => {
        history.push({
            pathname: `/t/results/upload/${studentId}`,
            state: { student },
        });
    };

    const fetchResults = async () => {
        setLoading(true);
        try {
            const term = selectedTerm.id || schoolTerm || '1';
            const session = selectedSession.id || schoolSession || '2024/2025';
            const { data } = await userRequest.get(`result/${school?._id}/${studentId}?class_id=${classId}&term=${term}&session=${session}`);
            console.log(data);
            setFullResults(data || {})
            setResults(data.results || []);
        } catch (error) {
            console.error("Error fetching results:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAttendance = async () => {
        try {
            const term = selectedTerm.id || schoolTerm || '1';
            const session = selectedSession.id || schoolSession || '2024/2025';
            const { data } = await userRequest.get(`/attendance/student/${studentId}?class_id=${classId}&school_id=${school?._id}&term=${term}&session=${session}&limit=50`);
            const attendanceRecords = data?.data || [];

            // Calculate attendance summary
            const daysPresent = attendanceRecords.reduce((total, record) => {
                return total + (record.days?.filter(day => day.status === 1).length || 0);
            }, 0);

            const daysAbsent = attendanceRecords.reduce((total, record) => {
                return total + (record.days?.filter(day => day.status === 2).length || 0);
            }, 0);

            const totalDays = attendanceRecords.reduce(
                (total, record) => total + (record.days?.length || 0),
                0
            );

            const attendanceRate = totalDays > 0 ? Math.round((daysPresent / totalDays) * 100) : 0;

            setAttendanceSummary({
                totalDays,
                daysPresent,
                daysAbsent,
                attendanceRate
            });
        } catch (error) {
            console.error("Error fetching attendance:", error);
            setAttendanceSummary(null);
        }
    };

    useEffect(() => {
        if (school?._id && selectedTerm.id && selectedSession.id) {
            fetchResults();
            fetchAttendance();
        }
    }, [school?._id, selectedTerm.id, selectedSession.id]);

    useEffect(() => {
        if (!studentId) return;
        userRequest.get(`/children/${studentId}`)
            .then(({ data }) => setStudentMiddlename(data?.middlename || ''))
            .catch((error) => console.error('Error fetching student middlename:', error));
    }, [studentId]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (termDropdown || sessionDropdown) {
                if (!event.target.closest('.dropdown-container')) {
                    setTermDropdown(false);
                    setSessionDropdown(false);
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [termDropdown, sessionDropdown]);

    // Term and Session handlers
    const handleTermDropdown = () => setTermDropdown(!termDropdown);
    const handleSessionDropdown = () => setSessionDropdown(!sessionDropdown);

    const handleTermSelection = (id, name) => {
        const newSelection = { id, name };
        setSelectedTerm(newSelection);
        localStorage.setItem('viewStudentResultSelectedTerm', JSON.stringify(newSelection));
        setTermDropdown(false);
    };

    const handleSessionSelection = (id, name) => {
        const newSelection = { id, name };
        setSelectedSession(newSelection);
        localStorage.setItem('viewStudentResultSelectedSession', JSON.stringify(newSelection));
        setSessionDropdown(false);
    };

    // Dynamically build CA columns based on school settings (for PDF export)
    const caColumns = Array.isArray(caSettings)
        ? caSettings.map((ca, idx) => ({
            name: ca.title ? `${ca.title} (${ca.max_score})` : `CA ${idx + 1} (${ca.max_score})`,
            caTitle: ca.title || `CA ${idx + 1}`,
            caIndex: idx + 1,
            maxScore: ca.max_score
        }))
        : [];

    // Full Result PDF Export
    const exportPDF = async () => {
        if (!fullResults || !fullResults.child || !fullResults.results || fullResults.results.length === 0) {
            alert("No results data available to export");
            return;
        }

        const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 12;
        const currentDate = new Date().toLocaleDateString('en-GB');

        // Extract colors from school logo
        let colors;
        try {
            const logoSource = school?.picture?.path || emmymaxLogo;
            colors = await extractLogoColors(logoSource);
        } catch (error) {
            console.error('Color extraction failed, using default palette:', error);
            colors = getDefaultColorPalette();
        }

        // Preload the logo/signature — addImage() can't fetch a remote URL itself.
        let logoDataUrl = null;
        try {
            logoDataUrl = await loadImageAsDataUrl(school?.picture?.path || emmymaxLogo);
        } catch (error) {
            console.error('Error preloading school logo for PDF:', error);
        }
        let signatureDataUrl = null;
        const signatureSrc = school?.admin_signature?.path;
        if (signatureSrc) {
            try {
                signatureDataUrl = await loadImageAsDataUrl(signatureSrc);
            } catch (error) {
                console.error('Error preloading signature for PDF:', error);
            }
        }
        let studentPhotoDataUrl = null;
        const studentPhotoUrl = fullResults.child?.picture?.path;
        if (studentPhotoUrl) {
            try {
                studentPhotoDataUrl = await loadImageAsDataUrl(studentPhotoUrl);
            } catch (error) {
                console.error('Error preloading student photo for PDF:', error);
            }
        }

        // Get assessment system for PDF export (use same logic as component level)
        const pdfClassObj = fullResults?.results?.[0]?.class;
        const pdfAssessmentSystem = pdfClassObj
            ? getAssessmentSystemForClass(pdfClassObj)
            : (school?.settings?.assessment?.assessment_system?.find(config => config.is_default === true) || { ca: { cas: [] }, exam: { max_score: 100 } });
        const pdfCaSettings = pdfAssessmentSystem?.ca?.cas || [];
        const pdfExamMaxScore = pdfAssessmentSystem?.exam?.max_score || 100;


        // Filter out subjects with total score of 0 (students who don't take those subjects)
        const validResults = (fullResults.results || []).filter(subjectResult => {
            const total = subjectResult.total;
            return total != null && total !== 0 && total !== '0';
        });

        // When a student takes many subjects, compact the fixed sections so the
        // subjects table can claim more space and stay on one page.
        const dense = validResults.length > 12;
        // A school-defined custom position (e.g. "First in Upper Grade 1") adds
        // an extra line to the student card, so grow the card to fit it.
        const showPosition = !!(school?.settings?.use_custom_position && fullResults.custom_position);
        const L = {
            card: { h: (dense ? 22 : 28) + (showPosition ? 4 : 0), nameFont: dense ? 13 : 16, subFont: dense ? 9 : 10 },
            attendance: { h: dense ? 15 : 20, titleFont: dense ? 9 : 10, metricFont: dense ? 7 : 8 },
            footer: { headerFont: dense ? 8.5 : 10, minH: dense ? 28 : 35, maxH: dense ? 64 : 72, commentFont: dense ? 6 : 6.5, commentLineH: dense ? 2.5 : 2.7 },
            ca: { rowH: dense ? 4.2 : 5, font: dense ? 7 : 8 },
            nextTerm: { h: dense ? 8 : 10, font: dense ? 8 : 9 },
        };

        // Header
        const addModernHeader = () => {
            let currentY = margin;
            const gradientHeight = 25;
            const gradientSteps = 50;

            for (let i = 0; i < gradientSteps; i++) {
                const ratio = i / gradientSteps;
                const r = Math.floor(colors.gradientStart[0] * (1 - ratio) + colors.gradientEnd[0] * ratio);
                const g = Math.floor(colors.gradientStart[1] * (1 - ratio) + colors.gradientEnd[1] * ratio);
                const b = Math.floor(colors.gradientStart[2] * (1 - ratio) + colors.gradientEnd[2] * ratio);
                doc.setFillColor(r, g, b);
                doc.rect(0, i * (gradientHeight / gradientSteps), pageWidth, gradientHeight / gradientSteps, 'F');
            }

            const logoWidth = 18;
            const logoHeight = 15;
            const logoX = margin;
            const logoY = 5;

            try {
                if (logoDataUrl) {
                    doc.addImage(logoDataUrl, "PNG", logoX, logoY, logoWidth, logoHeight);
                }
            } catch (error) {
                console.error("Error adding logo:", error);
            }

            doc.setTextColor(255, 255, 255);
            doc.setFontSize(16);
            doc.setFont(undefined, 'bold');
            doc.text(school?.school_name || "School Name", logoX + logoWidth + 8, 12);

            doc.setFontSize(9);
            doc.setFont(undefined, 'normal');
            const address = school?.address?.main?.address_line || "School Address";
            const addressLines = doc.splitTextToSize(address, 70);
            doc.text(addressLines, logoX + logoWidth + 8, 17);

            doc.setTextColor(255, 255, 255);
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');

            const currentTerm = selectedTerm.id || schoolTerm;
            const currentSession = selectedSession.id || schoolSession;
            let displayTerm = currentTerm;
            if ((school?.settings?.term_terminology || '').toLowerCase() === 'cycle') {
                if (currentTerm === '1' || currentTerm === 1) displayTerm = 'Chilly';
                else if (currentTerm === '2' || currentTerm === 2) displayTerm = 'Dusty';
                else if (currentTerm === '3' || currentTerm === 3) displayTerm = 'Rainy';
            }
            const termText = `${displayTerm} ${school?.settings?.term_terminology || 'Term'} | ${currentSession}`;
            const dateText = `Generated: ${currentDate}`;
            doc.text(termText, pageWidth - margin, 12, { align: "right" });
            doc.text(dateText, pageWidth - margin, 18, { align: "right" });

            return 30;
        };

        // Student card
        const addStudentCard = (startY) => {
            const cardHeight = L.card.h;
            const cardY = startY;

            doc.setFillColor(247, 247, 247);
            doc.roundedRect(margin, cardY, pageWidth - 2 * margin, cardHeight, 4, 4, 'F');
            doc.setFillColor(248, 249, 250);
            doc.roundedRect(margin + 0.5, cardY + 0.5, pageWidth - 2 * margin - 1, cardHeight - 1, 3.5, 3.5, 'F');

            // Add the preloaded student photo — sized to the card so it always fits its padding
            const photoSize = cardHeight - 6;
            const photoX = margin + 4;
            const photoY = cardY + 3;
            let hasPhoto = false;
            if (studentPhotoDataUrl) {
                try {
                    doc.addImage(studentPhotoDataUrl, 'PNG', photoX, photoY, photoSize, photoSize);
                    hasPhoto = true;
                } catch (err) {
                    console.error('Error adding student photo to PDF:', err);
                }
            }

            const infoStartX = hasPhoto ? photoX + photoSize + 4 : margin + 8;
            const studentName = getStudentFullName(fullResults.child, studentMiddlename);
            const studentAdmissionNo = fullResults.child?.registration_id || "N/A";
            const classInfo = fullResults.results[0]?.class?.name || "N/A";

            doc.setFontSize(L.card.nameFont);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(15, 23, 42);
            doc.text(studentName, infoStartX, cardY + (dense ? 8 : 9));

            doc.setFontSize(L.card.subFont);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(71, 85, 105);
            doc.text(`Class: ${classInfo}`, infoStartX, cardY + (dense ? 13 : 15));
            doc.text(`Admission No: ${studentAdmissionNo}`, infoStartX, cardY + (dense ? 18 : 20));

            const summaryWidth = 70;
            const summaryHeight = cardHeight - 6;
            const summaryX = pageWidth - margin - summaryWidth;
            const summaryY = cardY + 3;

            doc.setFillColor(248, 250, 252);
            doc.roundedRect(summaryX, summaryY, summaryWidth, summaryHeight, 3, 3, 'F');
            doc.setDrawColor(148, 163, 184);
            doc.setLineWidth(0.5);
            doc.roundedRect(summaryX, summaryY, summaryWidth, summaryHeight, 3, 3, 'S');

            // Use validResults (already filtered to exclude subjects with total === 0) for calculations
            const totalScore = validResults.reduce((sum, sub) => sum + (sub.total || 0), 0);
            const avgScore = validResults.length > 0 ? (totalScore / validResults.length).toFixed(1) : "0.0";

            doc.setFont(undefined, 'bold');
            doc.setFontSize(dense ? 9.5 : 11);
            doc.setTextColor(30, 41, 59);
            doc.text("Performance Overview", summaryX + summaryWidth / 2, summaryY + (dense ? 5 : 6), { align: "center" });

            doc.setFontSize(dense ? 8 : 9);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(71, 85, 105);
            const row1Y = summaryY + (dense ? 9.5 : 11);
            const row2Y = summaryY + (dense ? 13.5 : 15);
            const row3Y = summaryY + (dense ? 17.5 : 19);
            doc.text("Total Score:", summaryX + 4, row1Y);
            doc.text("Average:", summaryX + 4, row2Y);
            if (showPosition) doc.text("Position:", summaryX + 4, row3Y);

            doc.setFont(undefined, 'bold');
            doc.setTextColor(30, 41, 59);
            doc.text(`${totalScore}`, summaryX + summaryWidth - 4, row1Y, { align: "right" });
            doc.text(`${avgScore}%`, summaryX + summaryWidth - 4, row2Y, { align: "right" });
            if (showPosition) doc.text(`${fullResults.custom_position}`, summaryX + summaryWidth - 4, row3Y, { align: "right" });

            return cardY + cardHeight + 4;
        };

        // Subjects table — auto-fits into the remaining space to stay on one page.
        const addSubjectsTable = (startY, reservedBelow) => {
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(...colors.primaryDark);
            doc.text("Academic Performance", margin, startY);
            doc.setDrawColor(...colors.primary);
            doc.setLineWidth(1);
            doc.line(margin, startY + 2, margin + 60, startY + 2);

            // Group subjects by subject_grouping (keeping existing logic)
            // validResults already excludes subjects with total === 0
            const groupedSubjects = {};
            const ungroupedSubjects = [];
            validResults.forEach(subjectResult => {
                const group = subjectResult.subject?.subject_grouping;
                if (group && group._id) {
                    if (!groupedSubjects[group._id]) {
                        groupedSubjects[group._id] = { name: group.name, subjects: [] };
                    }
                    groupedSubjects[group._id].subjects.push(subjectResult);
                } else {
                    ungroupedSubjects.push(subjectResult);
                }
            });

            const getGradeColor = (grade) => {
                const gradeColors = {
                    'A': colors.success,
                    'A*': colors.success,
                    'B': colors.secondary,
                    'C': colors.warning,
                    'D': colors.warning,
                    'E': colors.accent,
                    'F': colors.accent
                };
                return gradeColors[grade] || colors.text;
            };

            // Build head/body for a given font config so the table can shrink to fit.
            const buildTable = (font) => {
                const caHeaders = caColumns.map(ca => ({
                    content: ca.name,
                    styles: { fontSize: font.head, fontStyle: 'bold', halign: 'center' }
                }));
                const tableHead = [
                    { content: 'Subject', styles: { fontSize: font.head, fontStyle: 'bold' } },
                    ...caHeaders,
                    { content: `Exam (${pdfExamMaxScore})`, styles: { fontSize: font.head, fontStyle: 'bold', halign: 'center' } },
                    { content: 'Total', styles: { fontSize: font.head, fontStyle: 'bold', halign: 'center' } },
                    { content: 'Grade', styles: { fontSize: font.head, fontStyle: 'bold', halign: 'center' } },
                    { content: 'Class Avg', styles: { fontSize: font.head, fontStyle: 'bold', halign: 'center' } },
                ];

                const buildSubjectRow = (subject) => {
                    const caCells = caColumns.map(ca => {
                        const caObj = Array.isArray(subject.cas) ? subject.cas.find(c => c.ca === ca.caIndex) : null;
                        return { content: (caObj && caObj.score != null) ? caObj.score.toString() : '0', styles: { fontSize: font.body, halign: 'center' } };
                    });
                    // Calculate grade from grading system instead of using backend grade
                    const calculatedGrade = calculateGrade(subject.total, gradingSystem);
                    const gradeColor = getGradeColor(calculatedGrade);
                    return [
                        { content: subject.subject.name, styles: { fontSize: font.body } },
                        ...caCells,
                        { content: subject.exam != null ? subject.exam.toString() : '0', styles: { fontSize: font.body, halign: 'center' } },
                        { content: subject.total != null ? subject.total.toString() : '0', styles: { fontSize: font.body, halign: 'center', fontStyle: 'bold' } },
                        { content: calculatedGrade, styles: { fontSize: font.body, halign: 'center', textColor: gradeColor, fontStyle: 'bold' } },
                        { content: (subject.class_average != null ? subject.class_average : '-').toString(), styles: { fontSize: font.body, halign: 'center' } },
                    ];
                };

                const tableBody = [];
                Object.values(groupedSubjects).forEach(group => {
                    tableBody.push([
                        {
                            content: group.name, colSpan: 6 + caColumns.length, styles: {
                                fontStyle: 'bold',
                                fillColor: colors.primary,
                                textColor: [255, 255, 255],
                                fontSize: font.head
                            }
                        }
                    ]);
                    group.subjects.forEach(subject => {
                        tableBody.push(buildSubjectRow(subject));
                    });
                });

                if (Object.keys(groupedSubjects).length > 0 && ungroupedSubjects.length > 0) {
                    tableBody.push([
                        {
                            content: '', colSpan: 6 + caColumns.length, styles: {
                                fontStyle: 'italic',
                                fillColor: colors.lightGray,
                                textColor: colors.darkGray,
                                fontSize: font.body,
                                halign: 'left'
                            }
                        }
                    ]);
                }

                ungroupedSubjects.forEach(subject => {
                    tableBody.push(buildSubjectRow(subject));
                });

                return { head: [tableHead], body: tableBody };
            };

            const tableBodyStartY = startY + 6;
            const availableHeight = Math.max(24, (pageHeight - margin) - tableBodyStartY - reservedBelow);
            const finalY = drawFittedTable(doc, {
                buildTable,
                startY: tableBodyStartY,
                availableHeight,
                tableOptions: {
                    theme: 'grid',
                    styles: { lineColor: colors.mediumGray, lineWidth: 0.1 },
                    headStyles: { fillColor: colors.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
                    alternateRowStyles: { fillColor: lightenColor(colors.primaryLight, 0.8) },
                    tableWidth: pageWidth - 2 * margin,
                    margin: { left: margin, right: margin },
                },
            });
            return finalY + 8;
        };

        // Attendance summary section
        const addAttendanceSection = (startY) => {
            const attData = attendanceSummary;
            if (!attData) return startY;

            const cardHeight = L.attendance.h;
            const cardWidth = pageWidth - 2 * margin;
            const cardY = startY;

            // Card background
            doc.setFillColor(248, 249, 250);
            doc.roundedRect(margin, cardY, cardWidth, cardHeight, 3, 3, 'F');

            // Border
            doc.setDrawColor(148, 163, 184);
            doc.setLineWidth(0.5);
            doc.roundedRect(margin, cardY, cardWidth, cardHeight, 3, 3, 'S');

            // Title
            doc.setFontSize(L.attendance.titleFont);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(30, 41, 59);
            doc.text("Attendance Summary", margin + 4, cardY + (dense ? 5 : 6));

            // Attendance metrics in a grid layout
            doc.setFontSize(L.attendance.metricFont);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(71, 85, 105);

            const metricsStartY = cardY + (dense ? 8.5 : 10);
            const metricsSpacing = dense ? 3 : 4;
            const leftColumnX = margin + 4;
            const rightColumnX = margin + cardWidth / 2;

            // Left column
            doc.text("Total Days Marked:", leftColumnX, metricsStartY);
            doc.text("Days Present:", leftColumnX, metricsStartY + metricsSpacing);
            doc.text("Days Absent:", leftColumnX, metricsStartY + metricsSpacing * 2);

            // Right column
            doc.text("Attendance Rate:", rightColumnX, metricsStartY);

            // Values with emphasis
            doc.setFont(undefined, 'bold');
            doc.setTextColor(30, 41, 59);
            doc.text(`${attData.totalDays || 0}`, leftColumnX + 35, metricsStartY, { align: "left" });
            doc.text(`${attData.daysPresent || 0}`, leftColumnX + 35, metricsStartY + metricsSpacing, { align: "left" });
            doc.text(`${attData.daysAbsent || 0}`, leftColumnX + 35, metricsStartY + metricsSpacing * 2, { align: "left" });
            doc.text(`${attData.attendanceRate || 0}%`, rightColumnX + 40, metricsStartY, { align: "left" });

            return cardY + cardHeight + 4;
        };

        // Footer section
        const addFooterSection = (startY) => {
            const sectionWidth = (pageWidth - 4 * margin) / 3;

            // Calculate required height for comments section first
            doc.setFontSize(L.footer.commentFont);
            doc.setFont(undefined, 'normal');
            const teacherComment = fullResults.comment || "No comment";
            const adminRemark = fullResults.remark || "No remark";
            const teacherLines = doc.splitTextToSize(teacherComment, sectionWidth - 4);
            const adminLines = doc.splitTextToSize(adminRemark, sectionWidth - 4);

            const lineHeight = L.footer.commentLineH;
            const teacherTextHeight = teacherLines.length * lineHeight;
            const adminTextHeight = adminLines.length * lineHeight;
            const commentsRequiredHeight = 12 + 3 + teacherTextHeight + 2 + 3 + adminTextHeight + 4;

            // Use the maximum required height for all sections to ensure alignment.
            // Capped so a long comment can't push past one page.
            const sectionHeight = Math.min(Math.max(L.footer.minH, commentsRequiredHeight), L.footer.maxH);

            doc.setFillColor(...lightenColor(colors.primaryLight, 0.8));
            doc.roundedRect(margin, startY, sectionWidth, sectionHeight, 2, 2, 'F');
            doc.setDrawColor(...colors.primary);
            doc.setLineWidth(0.5);
            doc.roundedRect(margin, startY, sectionWidth, sectionHeight, 2, 2, 'S');

            doc.setFontSize(L.footer.headerFont);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(...colors.primaryDark);
            doc.text("Skills Assessment", margin + 2, startY + 6);

            doc.setFontSize(7);
            doc.setFont(undefined, 'normal');
            let skillY = startY + 10;

            if (fullResults?.skills?.length) {
                const capitalizeWords = str => str.replace(/\b\w/g, c => c.toUpperCase());
                const getSkillColor = (rating) => {
                    const skillColors = {
                        'Excellent': colors.success,
                        'Very Good': colors.secondary,
                        'Good': colors.warning,
                        'Fair': colors.warning,
                        'Poor': colors.accent
                    };
                    return skillColors[rating] || colors.text;
                };
                fullResults.skills.slice(0, 5).forEach(skill => {
                    const skillColor = getSkillColor(skill.rating);
                    const skillName = capitalizeWords(skill.skill || "");
                    doc.setTextColor(...colors.text);
                    doc.text(`${skillName}:`, margin + 2, skillY);
                    doc.setTextColor(...skillColor);
                    doc.text(skill.rating, margin + 2 + doc.getTextWidth(`${skillName}: `), skillY);
                    skillY += 3.5;
                });
            } else {
                doc.text("No skills assessed", margin + 2, skillY);
            }

            let gradingX = margin + sectionWidth + margin;
            doc.setFillColor(...lightenColor(colors.secondaryLight, 0.8));
            doc.roundedRect(gradingX, startY, sectionWidth, sectionHeight, 2, 2, 'F');
            doc.setDrawColor(...colors.secondary);
            doc.setLineWidth(0.5);
            doc.roundedRect(gradingX, startY, sectionWidth, sectionHeight, 2, 2, 'S');

            doc.setFontSize(L.footer.headerFont);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(...colors.primaryDark);
            doc.text("Grading System", gradingX + 2, startY + 6);

            doc.setFontSize(7);
            doc.setFont(undefined, 'normal');
            let gradeY = startY + 10;
            const gradeLineHeight = 3.5;

            const getGradeColor = (grade) => {
                const gradeColors = {
                    'A': colors.success,
                    'A*': colors.success,
                    'B': colors.secondary,
                    'C': colors.warning,
                    'D': colors.warning,
                    'E': colors.accent,
                    'F': colors.accent
                };
                return gradeColors[grade] || colors.text;
            };

            gradingSystem.forEach((grade, idx) => {
                if (gradeY > startY + sectionHeight - gradeLineHeight) {
                    gradeY = startY + 10;
                    gradingX += 22;
                }
                const gradeColor = getGradeColor(grade.grade);
                doc.setTextColor(...gradeColor);
                doc.text(grade.grade, gradingX + 2, gradeY);
                doc.setTextColor(...colors.text);
                doc.text(`${grade.min_score}-${grade.max_score}`, gradingX + 12, gradeY);
                gradeY += gradeLineHeight;
            });

            const commentsX = margin + 2 * sectionWidth + 2 * margin;
            doc.setFillColor(...lightenColor(colors.accent, 0.9));
            doc.roundedRect(commentsX, startY, sectionWidth, sectionHeight, 2, 2, 'F');
            doc.setDrawColor(...colors.accent);
            doc.setLineWidth(0.5);
            doc.roundedRect(commentsX, startY, sectionWidth, sectionHeight, 2, 2, 'S');

            doc.setFontSize(L.footer.headerFont);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(...colors.primaryDark);
            doc.text("Comments", commentsX + 2, startY + 6);

            doc.setFontSize(L.footer.commentFont);
            doc.setFont(undefined, 'normal');

            // Teacher comment - display all lines
            let currentY = startY + 12;
            doc.setFont(undefined, 'bold');
            doc.setTextColor(...colors.text);
            doc.text("Teacher:", commentsX + 2, currentY);
            doc.setFont(undefined, 'normal');
            currentY += 3;

            // Display all teacher comment lines
            teacherLines.forEach((line, index) => {
                // Safety check: ensure we don't exceed section bounds
                if (currentY < startY + sectionHeight - 10) { // Leave space for admin section
                    doc.text(line, commentsX + 2, currentY);
                    currentY += lineHeight;
                }
            });

            // Admin remark - display all lines
            currentY += 2; // Add spacing between teacher and admin
            doc.setFont(undefined, 'bold');
            doc.text("Admin:", commentsX + 2, currentY);
            doc.setFont(undefined, 'normal');
            currentY += 3;

            // Display all admin remark lines
            adminLines.forEach((line, index) => {
                // Safety check: ensure we don't exceed section bounds
                if (currentY < startY + sectionHeight - 2) { // Leave small margin at bottom
                    doc.text(line, commentsX + 2, currentY);
                    currentY += lineHeight;
                }
            });

            return startY + sectionHeight + 4;
        };

        // Signature section
        const addSignatureSection = (startY) => {
            if (signatureDataUrl) {
                try {
                    const imgWidth = 25;
                    const imgHeight = 12;
                    const x = pageWidth - margin - imgWidth;
                    const y = startY;

                    doc.setFillColor(...lightenColor(colors.primary, 0.9));
                    doc.roundedRect(x - 2, y - 2, imgWidth + 4, imgHeight + 6, 2, 2, 'F');
                    doc.addImage(signatureDataUrl, 'PNG', x, y, imgWidth, imgHeight);
                    doc.setDrawColor(...colors.primary);
                    doc.setLineWidth(0.8);
                    doc.line(x, y + imgHeight + 2, x + imgWidth, y + imgHeight + 2);
                    doc.setFontSize(8);
                    doc.setTextColor(...colors.primaryDark);
                    doc.text('Principal/Head Teacher', x + imgWidth / 2, y + imgHeight + 6, { align: 'center' });
                } catch (e) {
                    console.error("Error adding signature:", e);
                }
            }
            doc.setFontSize(7);
            doc.setTextColor(...colors.darkGray);
            doc.text("Powered by Acceede.com", margin, pageHeight - 2);
            return startY + 20;
        };

        // CA description card (rendered below the table)
        const addCADescriptionCard = (startY) => {
            // Use CA data from current assessment system
            const caList = Array.isArray(pdfCaSettings) ? pdfCaSettings : [];
            const caDescriptions = caList
                .filter(ca => ca.description && ca.description.trim())
                .map(ca => ({ title: ca.title || `CA`, description: ca.description.trim() }));
            if (caDescriptions.length === 0) return startY;
            const itemsPerRow = 2;
            const itemWidth = (pageWidth - 2 * margin - 8) / itemsPerRow;
            const rowHeight = L.ca.rowH;
            const numRows = Math.ceil(caDescriptions.length / itemsPerRow);
            const cardHeight = 6 + numRows * rowHeight;
            doc.setFillColor(248, 249, 250);
            doc.roundedRect(margin, startY, pageWidth - 2 * margin, cardHeight, 3, 3, 'F');
            doc.setDrawColor(148, 163, 184);
            doc.setLineWidth(0.5);
            doc.roundedRect(margin, startY, pageWidth - 2 * margin, cardHeight, 3, 3, 'S');
            doc.setFontSize(L.ca.font);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(71, 85, 105);
            for (let i = 0; i < caDescriptions.length; i++) {
                const row = Math.floor(i / itemsPerRow);
                const col = i % itemsPerRow;
                const x = margin + 4 + col * (itemWidth + 4);
                const y = startY + 6 + row * rowHeight;
                doc.setFont(undefined, 'bold');
                doc.text(`${caDescriptions[i].title}:`, x, y);
                doc.setFont(undefined, 'normal');
                doc.text(caDescriptions[i].description, x + 32, y);
            }
            return startY + cardHeight + 4;
        };

        // Next term start date info
        const addNextTermInfo = (startY) => {
            const currentTermNum = parseInt(selectedTerm.id || schoolTerm);
            const terms = school?.settings?.terms || fullResults?.school?.settings?.terms || [];
            const nextTerm = terms.find(t => t.term === currentTermNum + 1);

            const cardHeight = L.nextTerm.h;
            const cardWidth = pageWidth - 2 * margin;

            doc.setFillColor(248, 249, 250);
            doc.roundedRect(margin, startY, cardWidth, cardHeight, 3, 3, 'F');
            doc.setDrawColor(148, 163, 184);
            doc.setLineWidth(0.5);
            doc.roundedRect(margin, startY, cardWidth, cardHeight, 3, 3, 'S');

            doc.setFontSize(L.nextTerm.font);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(30, 41, 59);

            const nextTermY = startY + (dense ? 5 : 6);
            if (nextTerm?.start_date) {
                const formattedDate = new Date(nextTerm.start_date)
                    .toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' });
                doc.text(`Next Term Begins: ${formattedDate}`, margin + 4, nextTermY);
            } else {
                const sess = selectedSession.id || schoolSession;
                doc.text(`End of Academic Session ${sess}`, margin + 4, nextTermY);
            }

            return startY + cardHeight + 4;
        };

        // Reserve the space every section below the table uses, so the table fits.
        const computeReservedBelow = () => {
            const attData = attendanceSummary;
            const attH = attData ? (L.attendance.h + 4) : 0;

            const sectionWidth = (pageWidth - 4 * margin) / 3;
            doc.setFontSize(L.footer.commentFont); doc.setFont(undefined, 'normal');
            const teacherLines = doc.splitTextToSize(fullResults.comment || "No comment", sectionWidth - 4);
            const adminLines = doc.splitTextToSize(fullResults.remark || "No remark", sectionWidth - 4);
            const commentsRequiredHeight = 12 + 3 + teacherLines.length * L.footer.commentLineH + 2 + 3 + adminLines.length * L.footer.commentLineH + 4;
            const footerH = Math.min(Math.max(L.footer.minH, commentsRequiredHeight), L.footer.maxH) + 4;

            const caDescriptions = (Array.isArray(pdfCaSettings) ? pdfCaSettings : [])
                .filter(ca => ca.description && ca.description.trim());
            const caH = caDescriptions.length ? (6 + Math.ceil(caDescriptions.length / 2) * L.ca.rowH + 4) : 0;

            const nextTermH = L.nextTerm.h + 4;
            const signatureH = 20;
            const safety = 4;
            return attH + footerH + caH + nextTermH + signatureH + safety;
        };

        // Build the PDF as a single page
        let currentY = addModernHeader();
        currentY = addStudentCard(currentY);
        currentY = addSubjectsTable(currentY, computeReservedBelow());
        currentY = addAttendanceSection(currentY);
        currentY = addFooterSection(currentY);
        currentY = addCADescriptionCard(currentY);
        currentY = addNextTermInfo(currentY);
        addSignatureSection(currentY);

        const studentName = getStudentFullName(fullResults.child, studentMiddlename);
        const currentTerm = selectedTerm.id || schoolTerm;
        const currentSession = selectedSession.id || schoolSession;
        doc.save(`${studentName}_Academic_Report_${currentTerm}term_${currentSession}.pdf`);
    };

    // Mid-term Results PDF export
    const exportMidTermPDF = async () => {
        if (!fullResults || !fullResults.child || !fullResults.results || fullResults.results.length === 0) {
            alert("No results data available to export");
            return;
        }

        const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 12;
        const currentDate = new Date().toLocaleDateString('en-GB');

        let colors;
        try {
            const logoSource = school?.picture?.path || emmymaxLogo;
            colors = await extractLogoColors(logoSource);
        } catch (error) {
            console.error('Color extraction failed, using default palette:', error);
            colors = getDefaultColorPalette();
        }

        // Preload the logo/signature — addImage() can't fetch a remote URL itself.
        let logoDataUrl = null;
        try {
            logoDataUrl = await loadImageAsDataUrl(school?.picture?.path || emmymaxLogo);
        } catch (error) {
            console.error('Error preloading school logo for PDF:', error);
        }
        let signatureDataUrl = null;
        const signatureSrc = school?.admin_signature?.path;
        if (signatureSrc) {
            try {
                signatureDataUrl = await loadImageAsDataUrl(signatureSrc);
            } catch (error) {
                console.error('Error preloading signature for PDF:', error);
            }
        }
        let studentPhotoDataUrl = null;
        const studentPhotoUrl = fullResults.child?.picture?.path;
        if (studentPhotoUrl) {
            try {
                studentPhotoDataUrl = await loadImageAsDataUrl(studentPhotoUrl);
            } catch (error) {
                console.error('Error preloading student photo for PDF:', error);
            }
        }

        // Compact the fixed sections when there are many subjects so the table
        // gets more room and the report stays on one page.
        const midTermValidCount = (fullResults.results || []).filter(subjectResult => {
            const caTotal = Array.isArray(subjectResult.cas)
                ? subjectResult.cas.reduce((sum, caObj) => sum + (caObj.score || 0), 0)
                : 0;
            return caTotal !== 0;
        }).length;
        const dense = midTermValidCount > 12;
        const L = {
            card: { h: dense ? 22 : 28, nameFont: dense ? 13 : 16, subFont: dense ? 9 : 10 },
            attendance: { h: dense ? 15 : 20, titleFont: dense ? 9 : 10, metricFont: dense ? 7 : 8 },
            nextTerm: { h: dense ? 8 : 10, font: dense ? 8 : 9 },
        };

        const addModernHeader = () => {
            let currentY = margin;
            const gradientHeight = 25;
            const gradientSteps = 50;

            for (let i = 0; i < gradientSteps; i++) {
                const ratio = i / gradientSteps;
                const r = Math.floor(colors.gradientStart[0] * (1 - ratio) + colors.gradientEnd[0] * ratio);
                const g = Math.floor(colors.gradientStart[1] * (1 - ratio) + colors.gradientEnd[1] * ratio);
                const b = Math.floor(colors.gradientStart[2] * (1 - ratio) + colors.gradientEnd[2] * ratio);
                doc.setFillColor(r, g, b);
                doc.rect(0, i * (gradientHeight / gradientSteps), pageWidth, gradientHeight / gradientSteps, 'F');
            }

            const logoWidth = 18;
            const logoHeight = 15;
            const logoX = margin;
            const logoY = 5;

            try {
                if (logoDataUrl) {
                    doc.addImage(logoDataUrl, "PNG", logoX, logoY, logoWidth, logoHeight);
                }
            } catch (error) {
                console.error("Error adding logo:", error);
            }

            doc.setTextColor(255, 255, 255);
            doc.setFontSize(16);
            doc.setFont(undefined, 'bold');
            doc.text(school?.school_name || "School Name", logoX + logoWidth + 8, 12);

            doc.setFontSize(9);
            doc.setFont(undefined, 'normal');
            const address = school?.address?.main?.address_line || "School Address";
            const addressLines = doc.splitTextToSize(address, 70);
            doc.text(addressLines, logoX + logoWidth + 8, 17);

            doc.setTextColor(255, 255, 255);
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');

            const currentTerm = selectedTerm.id || schoolTerm;
            const currentSession = selectedSession.id || schoolSession;
            let displayTerm = currentTerm;
            if ((school?.settings?.term_terminology || '').toLowerCase() === 'cycle') {
                if (currentTerm === '1' || currentTerm === 1) displayTerm = 'Chilly';
                else if (currentTerm === '2' || currentTerm === 2) displayTerm = 'Dusty';
                else if (currentTerm === '3' || currentTerm === 3) displayTerm = 'Rainy';
            }
            const termText = `${displayTerm} ${school?.settings?.term_terminology || 'Term'} | ${currentSession}`;
            const dateText = `Generated: ${currentDate}`;
            const reportType = "Mid-Term Results (Tests Only)";
            doc.text(reportType, pageWidth - margin, 8, { align: "right" });
            doc.text(termText, pageWidth - margin, 12, { align: "right" });
            doc.text(dateText, pageWidth - margin, 18, { align: "right" });

            return 30;
        };

        const addStudentCard = (startY) => {
            const cardHeight = L.card.h;
            const cardY = startY;

            doc.setFillColor(247, 247, 247);
            doc.roundedRect(margin, cardY, pageWidth - 2 * margin, cardHeight, 4, 4, 'F');
            doc.setFillColor(248, 249, 250);
            doc.roundedRect(margin + 0.5, cardY + 0.5, pageWidth - 2 * margin - 1, cardHeight - 1, 3.5, 3.5, 'F');

            // Add the preloaded student photo — sized to the card so it always fits its padding
            const photoSize = cardHeight - 6;
            const photoX = margin + 4;
            const photoY = cardY + 3;
            let hasPhoto = false;
            if (studentPhotoDataUrl) {
                try {
                    doc.addImage(studentPhotoDataUrl, 'PNG', photoX, photoY, photoSize, photoSize);
                    hasPhoto = true;
                } catch (err) {
                    console.error('Error adding student photo to PDF:', err);
                }
            }

            const infoStartX = hasPhoto ? photoX + photoSize + 4 : margin + 8;
            const studentName = getStudentFullName(fullResults.child, studentMiddlename);
            const studentAdmissionNo = fullResults.child?.registration_id || "N/A";
            const classInfo = fullResults.results[0]?.class?.name || "N/A";

            doc.setFontSize(L.card.nameFont);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(15, 23, 42);
            doc.text(studentName, infoStartX, cardY + (dense ? 8 : 9));

            doc.setFontSize(L.card.subFont);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(71, 85, 105);
            doc.text(`Class: ${classInfo}`, infoStartX, cardY + (dense ? 13 : 15));
            doc.text(`Admission No: ${studentAdmissionNo}`, infoStartX, cardY + (dense ? 18 : 20));

            return cardY + cardHeight + 4;
        };

        const addMidTermTable = (startY, reservedBelow) => {
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(...colors.primaryDark);
            doc.text("Mid-Term Test Results", margin, startY);
            doc.setDrawColor(...colors.primary);
            doc.setLineWidth(1);
            doc.line(margin, startY + 2, margin + 60, startY + 2);

            // Filter out subjects with total score of 0 (students who don't take those subjects)
            const validResults = (fullResults.results || []).filter(subjectResult => {
                // For mid-term, calculate CA total and exclude if it's 0
                const caTotal = Array.isArray(subjectResult.cas)
                    ? subjectResult.cas.reduce((sum, caObj) => sum + (caObj.score || 0), 0)
                    : 0;
                return caTotal !== 0;
            });

            // Group subjects by subject_grouping (same logic as full result)
            const groupedSubjects = {};
            const ungroupedSubjects = [];
            validResults.forEach(subjectResult => {
                const group = subjectResult.subject?.subject_grouping;
                if (group && group._id) {
                    if (!groupedSubjects[group._id]) {
                        groupedSubjects[group._id] = { name: group.name, subjects: [] };
                    }
                    groupedSubjects[group._id].subjects.push(subjectResult);
                } else {
                    ungroupedSubjects.push(subjectResult);
                }
            });

            const buildTable = (font) => {
                const caHeaders = caColumns.map(ca => ({
                    content: ca.name,
                    styles: { fontSize: font.head, fontStyle: 'bold', halign: 'center' }
                }));

                const tableHead = [
                    { content: 'Subject', styles: { fontSize: font.head, fontStyle: 'bold' } },
                    ...caHeaders,
                    { content: 'Total', styles: { fontSize: font.head, fontStyle: 'bold', halign: 'center' } }
                ];

                const buildSubjectRow = (subject) => {
                    const caCells = caColumns.map(ca => {
                        const caObj = Array.isArray(subject.cas) ? subject.cas.find(c => c.ca === ca.caIndex) : null;
                        return { content: (caObj && caObj.score != null) ? caObj.score.toString() : '0', styles: { fontSize: font.body, halign: 'center' } };
                    });

                    const caTotal = Array.isArray(subject.cas)
                        ? subject.cas.reduce((sum, caObj) => sum + (caObj.score || 0), 0)
                        : 0;

                    return [
                        { content: subject.subject.name, styles: { fontSize: font.body } },
                        ...caCells,
                        { content: caTotal.toString(), styles: { fontSize: font.body, halign: 'center', fontStyle: 'bold' } }
                    ];
                };

                const tableBody = [];
                Object.values(groupedSubjects).forEach(group => {
                    tableBody.push([
                        {
                            content: group.name, colSpan: caColumns.length + 2, styles: {
                                fontStyle: 'bold',
                                fillColor: colors.primary,
                                textColor: [255, 255, 255],
                                fontSize: font.head
                            }
                        }
                    ]);
                    group.subjects.forEach(subject => {
                        tableBody.push(buildSubjectRow(subject));
                    });
                });

                if (Object.keys(groupedSubjects).length > 0 && ungroupedSubjects.length > 0) {
                    tableBody.push([
                        {
                            content: '', colSpan: caColumns.length + 2, styles: {
                                fontStyle: 'italic',
                                fillColor: colors.lightGray,
                                textColor: colors.darkGray,
                                fontSize: font.body,
                                halign: 'left'
                            }
                        }
                    ]);
                }

                ungroupedSubjects.forEach(subject => {
                    tableBody.push(buildSubjectRow(subject));
                });

                return { head: [tableHead], body: tableBody };
            };

            const tableBodyStartY = startY + 6;
            const availableHeight = Math.max(24, (pageHeight - margin) - tableBodyStartY - reservedBelow);
            const finalY = drawFittedTable(doc, {
                buildTable,
                startY: tableBodyStartY,
                availableHeight,
                base: { body: 8, head: 8, pad: 2 },
                tableOptions: {
                    theme: 'grid',
                    styles: { lineColor: colors.mediumGray, lineWidth: 0.1 },
                    headStyles: { fillColor: colors.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
                    alternateRowStyles: { fillColor: lightenColor(colors.primaryLight, 0.8) },
                    tableWidth: pageWidth - 2 * margin,
                    margin: { left: margin, right: margin },
                },
            });

            return finalY + 8;
        };

        // Attendance summary section (same as full result)
        const addAttendanceSection = (startY) => {
            const attData = attendanceSummary;
            if (!attData) return startY;

            const cardHeight = L.attendance.h;
            const cardWidth = pageWidth - 2 * margin;
            const cardY = startY;

            // Card background
            doc.setFillColor(248, 249, 250);
            doc.roundedRect(margin, cardY, cardWidth, cardHeight, 3, 3, 'F');

            // Border
            doc.setDrawColor(148, 163, 184);
            doc.setLineWidth(0.5);
            doc.roundedRect(margin, cardY, cardWidth, cardHeight, 3, 3, 'S');

            // Title
            doc.setFontSize(L.attendance.titleFont);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(30, 41, 59);
            doc.text("Attendance Summary", margin + 4, cardY + (dense ? 5 : 6));

            // Attendance metrics in a grid layout
            doc.setFontSize(L.attendance.metricFont);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(71, 85, 105);

            const metricsStartY = cardY + (dense ? 8.5 : 10);
            const metricsSpacing = dense ? 3 : 4;
            const leftColumnX = margin + 4;
            const rightColumnX = margin + cardWidth / 2;

            // Left column
            doc.text("Total Days Marked:", leftColumnX, metricsStartY);
            doc.text("Days Present:", leftColumnX, metricsStartY + metricsSpacing);
            doc.text("Days Absent:", leftColumnX, metricsStartY + metricsSpacing * 2);

            // Right column
            doc.text("Attendance Rate:", rightColumnX, metricsStartY);

            // Values with emphasis
            doc.setFont(undefined, 'bold');
            doc.setTextColor(30, 41, 59);
            doc.text(`${attData.totalDays || 0}`, leftColumnX + 35, metricsStartY, { align: "left" });
            doc.text(`${attData.daysPresent || 0}`, leftColumnX + 35, metricsStartY + metricsSpacing, { align: "left" });
            doc.text(`${attData.daysAbsent || 0}`, leftColumnX + 35, metricsStartY + metricsSpacing * 2, { align: "left" });
            doc.text(`${attData.attendanceRate || 0}%`, rightColumnX + 40, metricsStartY, { align: "left" });

            return cardY + cardHeight + 4;
        };

        const addSignatureSection = (startY) => {
            if (signatureDataUrl) {
                try {
                    const imgWidth = 25;
                    const imgHeight = 12;
                    const x = pageWidth - margin - imgWidth;
                    const y = startY;

                    doc.setFillColor(...lightenColor(colors.primary, 0.9));
                    doc.roundedRect(x - 2, y - 2, imgWidth + 4, imgHeight + 6, 2, 2, 'F');
                    doc.addImage(signatureDataUrl, 'PNG', x, y, imgWidth, imgHeight);
                    doc.setDrawColor(...colors.primary);
                    doc.setLineWidth(0.8);
                    doc.line(x, y + imgHeight + 2, x + imgWidth, y + imgHeight + 2);
                    doc.setFontSize(8);
                    doc.setTextColor(...colors.primaryDark);
                    doc.text('Principal/Head Teacher', x + imgWidth / 2, y + imgHeight + 6, { align: 'center' });
                } catch (e) {
                    console.error("Error adding signature:", e);
                }
            }
            doc.setFontSize(7);
            doc.setTextColor(...colors.darkGray);
            doc.text("Powered by Acceede.com", margin, pageHeight - 2);
            return startY + 20;
        };

        // Next term start date info
        const addNextTermInfo = (startY) => {
            const currentTermNum = parseInt(selectedTerm.id || schoolTerm);
            const terms = school?.settings?.terms || fullResults?.school?.settings?.terms || [];
            const nextTerm = terms.find(t => t.term === currentTermNum + 1);

            const cardHeight = L.nextTerm.h;
            const cardWidth = pageWidth - 2 * margin;

            doc.setFillColor(248, 249, 250);
            doc.roundedRect(margin, startY, cardWidth, cardHeight, 3, 3, 'F');
            doc.setDrawColor(148, 163, 184);
            doc.setLineWidth(0.5);
            doc.roundedRect(margin, startY, cardWidth, cardHeight, 3, 3, 'S');

            doc.setFontSize(L.nextTerm.font);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(30, 41, 59);

            const nextTermY = startY + (dense ? 5 : 6);
            if (nextTerm?.start_date) {
                const formattedDate = new Date(nextTerm.start_date)
                    .toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' });
                doc.text(`Next Term Begins: ${formattedDate}`, margin + 4, nextTermY);
            } else {
                const sess = selectedSession.id || schoolSession;
                doc.text(`End of Academic Session ${sess}`, margin + 4, nextTermY);
            }

            return startY + cardHeight + 4;
        };

        // Reserve the space the sections below the table occupy, so the table fits.
        const computeReservedBelow = () => {
            const attData = attendanceSummary;
            const attH = attData ? (L.attendance.h + 4) : 0;
            const nextTermH = L.nextTerm.h + 4;
            const signatureH = 20;
            const safety = 4;
            return attH + nextTermH + signatureH + safety;
        };

        // Build the PDF as a single page
        let currentY = addModernHeader();
        currentY = addStudentCard(currentY);
        currentY = addMidTermTable(currentY, computeReservedBelow());
        currentY = addAttendanceSection(currentY);
        currentY = addNextTermInfo(currentY);
        addSignatureSection(currentY);

        const studentName = getStudentFullName(fullResults.child, studentMiddlename);
        const currentTerm = selectedTerm.id || schoolTerm;
        const currentSession = selectedSession.id || schoolSession;
        doc.save(`${studentName}_MidTerm_Results_${currentTerm}term_${currentSession}.pdf`);
    };

    const exportCumulativePDF = async () => {
        const session = selectedSession.id || schoolSession || '2024/2025';

        let cumulativeData;
        try {
            const { data } = await userRequest.get(`/result/cumulative/${school?._id}/${studentId}?session=${session}`);
            cumulativeData = data;
        } catch (error) {
            console.error('Error fetching cumulative result:', error);
            alert('Failed to fetch cumulative result. Please try again.');
            return;
        }

        const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 12;
        const currentDate = new Date().toLocaleDateString('en-GB');

        let colors;
        try {
            colors = await extractLogoColors(school?.picture?.path || emmymaxLogo);
        } catch {
            colors = getDefaultColorPalette();
        }

        // Preload the logo/signature — addImage() can't fetch a remote URL itself.
        let logoDataUrl = null;
        try {
            logoDataUrl = await loadImageAsDataUrl(school?.picture?.path || emmymaxLogo);
        } catch (error) {
            console.error('Error preloading school logo for PDF:', error);
        }
        let signatureDataUrl = null;
        const signatureSrc = school?.admin_signature?.path;
        if (signatureSrc) {
            try {
                signatureDataUrl = await loadImageAsDataUrl(signatureSrc);
            } catch (error) {
                console.error('Error preloading signature for PDF:', error);
            }
        }
        // Preload the student photo — addImage() can't fetch a remote URL itself.
        let studentPhotoDataUrl = null;
        const studentPhotoUrl = cumulativeData?.student?.child?.picture?.path;
        if (studentPhotoUrl) {
            try {
                studentPhotoDataUrl = await loadImageAsDataUrl(studentPhotoUrl);
            } catch (error) {
                console.error('Error preloading student photo for PDF:', error);
            }
        }

        const termLabel = school?.settings?.term_terminology || 'Term';
        const child = cumulativeData?.student?.child;
        const studentClass = cumulativeData?.student?.class || '';
        // Filter out subjects with a cumulative average of 0 (student didn't take those
        // subjects), matching the same "total score of 0" filtering as the standard result.
        const cumResults = (cumulativeData?.cumulativeResults || []).filter(row => {
            const avg = row.cumulative?.average;
            return avg != null && avg !== 0 && avg !== '0';
        });
        const termsSummary = cumulativeData?.termsSummary || [];

        const t3CaCount = Math.max(
            caSettings.length,
            ...cumResults.map(r => r.term3?.cas?.length || 0),
            0
        );

        // Compact the fixed sections when there are many subjects so the table
        // gets more room and the cumulative report stays on one page.
        const dense = cumResults.length > 12;
        // A school-defined custom position (e.g. "First in Upper Grade 1") adds
        // an extra line to the student card, so grow the card to fit it. The
        // cumulative report's position comes from the term 3 summary.
        const showPosition = !!(school?.settings?.use_custom_position && termsSummary.find(s => s.term === 3)?.custom_position);
        const L = {
            card: { h: (dense ? 20 : 24) + (showPosition ? 4 : 0), nameFont: dense ? 13 : 15, subFont: dense ? 8 : 9 },
            attendance: { h: dense ? 15 : 20, titleFont: dense ? 9 : 10, metricFont: dense ? 7 : 8 },
            footer: { headerFont: dense ? 8.5 : 10, minH: dense ? 28 : 35, maxH: dense ? 64 : 72, commentFont: dense ? 6 : 6.5, commentLineH: dense ? 2.5 : 2.7 },
        };

        const addHeader = () => {
            const gradientHeight = 25;
            for (let i = 0; i < 50; i++) {
                const ratio = i / 50;
                const r = Math.floor(colors.gradientStart[0] * (1 - ratio) + colors.gradientEnd[0] * ratio);
                const g = Math.floor(colors.gradientStart[1] * (1 - ratio) + colors.gradientEnd[1] * ratio);
                const b = Math.floor(colors.gradientStart[2] * (1 - ratio) + colors.gradientEnd[2] * ratio);
                doc.setFillColor(r, g, b);
                doc.rect(0, i * (gradientHeight / 50), pageWidth, gradientHeight / 50, 'F');
            }
            try {
                if (logoDataUrl) doc.addImage(logoDataUrl, 'PNG', margin, 5, 18, 15);
            } catch {}
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(16); doc.setFont(undefined, 'bold');
            doc.text(school?.school_name || 'School Name', margin + 22, 12);
            doc.setFontSize(9); doc.setFont(undefined, 'normal');
            const addr = school?.address?.main?.address_line || '';
            if (addr) doc.text(doc.splitTextToSize(addr, 70), margin + 22, 17);
            doc.setFontSize(10); doc.setFont(undefined, 'bold');
            doc.text(`Cumulative Report | ${session}`, pageWidth - margin, 12, { align: 'right' });
            doc.setFontSize(8); doc.setFont(undefined, 'normal');
            doc.text(`Generated: ${currentDate}`, pageWidth - margin, 18, { align: 'right' });
            return 30;
        };

        const addStudentCard = (startY) => {
            const cardH = L.card.h;
            doc.setFillColor(247, 247, 247);
            doc.roundedRect(margin, startY, pageWidth - 2 * margin, cardH, 4, 4, 'F');
            doc.setFillColor(248, 249, 250);
            doc.roundedRect(margin + 0.5, startY + 0.5, pageWidth - 2 * margin - 1, cardH - 1, 3.5, 3.5, 'F');

            // The preloaded student photo, sized to the card as on the mid-term and full results.
            const photoSize = cardH - 6;
            const photoX = margin + 4;
            let hasPhoto = false;
            if (studentPhotoDataUrl) {
                try {
                    doc.addImage(studentPhotoDataUrl, 'PNG', photoX, startY + 3, photoSize, photoSize);
                    hasPhoto = true;
                } catch (err) {
                    console.error('Error adding student photo to PDF:', err);
                }
            }
            const infoX = hasPhoto ? photoX + photoSize + 4 : margin + 6;
            const pillStartX = pageWidth - margin - 90;

            const studentName = getStudentFullName(child, studentMiddlename);
            doc.setFontSize(L.card.nameFont); doc.setFont(undefined, 'bold'); doc.setTextColor(15, 23, 42);
            // The photo leaves less room before the term boxes: a long name shrinks, then wraps upwards.
            const nameRoom = pillStartX - infoX - 2;
            for (let size = L.card.nameFont; size > 9 && doc.getTextWidth(studentName) > nameRoom; size -= 0.5) {
                doc.setFontSize(size - 0.5);
            }
            const nameLines = doc.splitTextToSize(studentName, nameRoom);
            const nameLineHeight = doc.getLineHeight() / doc.internal.scaleFactor;
            doc.text(nameLines, infoX, startY + (dense ? 8 : 9) - (nameLines.length - 1) * nameLineHeight);
            doc.setFontSize(L.card.subFont); doc.setFont(undefined, 'normal'); doc.setTextColor(71, 85, 105);
            doc.text(`Class: ${studentClass}`, infoX, startY + (dense ? 13 : 15));
            doc.text(`Session: ${session}`, infoX, startY + (dense ? 17.5 : 20));
            if (showPosition) {
                const t3Summary = termsSummary.find(s => s.term === 3);
                doc.setFont(undefined, 'bold'); doc.setTextColor(30, 41, 59);
                doc.text(`Position: ${t3Summary?.custom_position}`, infoX, startY + (dense ? 22 : 24));
            }

            const pillY = startY + (dense ? 4 : 5);
            const pillH = dense ? 12 : 14;
            [1, 2, 3].forEach((term, i) => {
                const summary = termsSummary.find(s => s.term === term);
                const total = summary?.overall_total ?? '-';
                const px = pillStartX + i * 31;
                doc.setFillColor(248, 250, 252);
                doc.roundedRect(px, pillY, 28, pillH, 2, 2, 'F');
                doc.setDrawColor(148, 163, 184); doc.setLineWidth(0.3);
                doc.roundedRect(px, pillY, 28, pillH, 2, 2, 'S');
                doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(71, 85, 105);
                doc.text(`${termLabel} ${term}`, px + 14, pillY + (dense ? 4 : 4.5), { align: 'center' });
                doc.setFontSize(9); doc.setFont(undefined, 'bold'); doc.setTextColor(30, 41, 59);
                doc.text(`${total}`, px + 14, pillY + (dense ? 8.5 : 9.5), { align: 'center' });
            });

            return startY + cardH + 5;
        };

        const addTable = (startY, reservedBelow) => {
            doc.setFontSize(11); doc.setFont(undefined, 'bold');
            doc.setTextColor(...colors.primaryDark);
            doc.text('Cumulative Academic Result', margin, startY);
            doc.setDrawColor(...colors.primary); doc.setLineWidth(1);
            doc.line(margin, startY + 2, margin + 70, startY + 2);

            const totalCols = t3CaCount + 6;

            const groupedSubjects = {};
            const ungroupedSubjects = [];
            cumResults.forEach(row => {
                const group = row.subject?.subject_grouping;
                if (group && group._id) {
                    if (!groupedSubjects[group._id]) groupedSubjects[group._id] = { name: group.name, rows: [] };
                    groupedSubjects[group._id].rows.push(row);
                } else {
                    ungroupedSubjects.push(row);
                }
            });

            // Build head/body for a given font config so the table can shrink to fit.
            const buildTable = (font) => {
                const headerRow1 = [
                    { content: 'Subject', rowSpan: 2, styles: { valign: 'middle', fontStyle: 'bold', fontSize: font.head } },
                    { content: `${termLabel} 1`, rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontStyle: 'bold', fontSize: font.head } },
                    { content: `${termLabel} 2`, rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontStyle: 'bold', fontSize: font.head } },
                    { content: `${termLabel} 3`, colSpan: t3CaCount + 2, styles: { halign: 'center', fontStyle: 'bold', fontSize: font.head } },
                    { content: 'Average', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontStyle: 'bold', fontSize: font.head } },
                ];

                const headerRow2 = [];
                for (let i = 0; i < t3CaCount; i++) {
                    const caLabel = caSettings[i]?.title || `CA${i + 1}`;
                    headerRow2.push({ content: caLabel, styles: { halign: 'center', fontSize: font.body } });
                }
                headerRow2.push({ content: 'Exam', styles: { halign: 'center', fontSize: font.body } });
                headerRow2.push({ content: 'Total', styles: { halign: 'center', fontSize: font.body, fontStyle: 'bold' } });

                const buildSubjectRow = (row) => {
                    const t1Total = row.term1?.total ?? null;
                    const t2Total = row.term2?.total ?? null;
                    const t3 = row.term3;
                    const avg = row.cumulative?.average;
                    const caCells = [];
                    for (let i = 0; i < t3CaCount; i++) {
                        const caObj = t3?.cas?.find(c => c.ca === i + 1);
                        caCells.push({ content: caObj?.score != null ? String(caObj.score) : '-', styles: { halign: 'center', fontSize: font.body } });
                    }
                    return [
                        { content: row.subject?.name || '-', styles: { fontSize: font.body } },
                        { content: t1Total != null ? String(t1Total) : '-', styles: { halign: 'center', fontSize: font.body } },
                        { content: t2Total != null ? String(t2Total) : '-', styles: { halign: 'center', fontSize: font.body } },
                        ...caCells,
                        { content: t3?.exam != null ? String(t3.exam) : '-', styles: { halign: 'center', fontSize: font.body } },
                        { content: t3?.total != null ? String(t3.total) : '-', styles: { halign: 'center', fontSize: font.body, fontStyle: 'bold' } },
                        { content: avg != null ? String(avg) : '-', styles: { halign: 'center', fontSize: font.body, fontStyle: 'bold', textColor: colors.primary } },
                    ];
                };

                const tableBody = [];
                Object.values(groupedSubjects).forEach(group => {
                    tableBody.push([{
                        content: group.name,
                        colSpan: totalCols,
                        styles: { fontStyle: 'bold', fillColor: colors.primary, textColor: [255, 255, 255], fontSize: font.head },
                    }]);
                    group.rows.forEach(row => tableBody.push(buildSubjectRow(row)));
                });
                if (Object.keys(groupedSubjects).length > 0 && ungroupedSubjects.length > 0) {
                    tableBody.push([{
                        content: '',
                        colSpan: totalCols,
                        styles: { fillColor: colors.lightGray, fontSize: font.body },
                    }]);
                }
                ungroupedSubjects.forEach(row => tableBody.push(buildSubjectRow(row)));

                return { head: [headerRow1, headerRow2], body: tableBody };
            };

            const tableBodyStartY = startY + 6;
            const availableHeight = Math.max(24, (pageHeight - margin) - tableBodyStartY - reservedBelow);
            const finalY = drawFittedTable(doc, {
                buildTable,
                startY: tableBodyStartY,
                availableHeight,
                base: { body: 7, head: 8, pad: 2 },
                tableOptions: {
                    theme: 'grid',
                    styles: { lineColor: colors.mediumGray, lineWidth: 0.1 },
                    headStyles: { fillColor: colors.primary, textColor: [255, 255, 255], fontStyle: 'bold' },
                    alternateRowStyles: { fillColor: lightenColor(colors.primaryLight, 0.8) },
                    tableWidth: pageWidth - 2 * margin,
                    margin: { left: margin, right: margin },
                },
            });

            return finalY + 8;
        };

        const addAttendanceSection = (startY) => {
            const attData = attendanceSummary;
            if (!attData) return startY;
            const cardHeight = L.attendance.h;
            const cardWidth = pageWidth - 2 * margin;
            doc.setFillColor(248, 249, 250);
            doc.roundedRect(margin, startY, cardWidth, cardHeight, 3, 3, 'F');
            doc.setDrawColor(148, 163, 184); doc.setLineWidth(0.5);
            doc.roundedRect(margin, startY, cardWidth, cardHeight, 3, 3, 'S');
            doc.setFontSize(L.attendance.titleFont); doc.setFont(undefined, 'bold'); doc.setTextColor(30, 41, 59);
            doc.text('Attendance Summary', margin + 4, startY + (dense ? 5 : 6));
            doc.setFontSize(L.attendance.metricFont); doc.setFont(undefined, 'normal'); doc.setTextColor(71, 85, 105);
            const mY = startY + (dense ? 8.5 : 10);
            const sp = dense ? 3 : 4;
            const lx = margin + 4;
            const rx = margin + cardWidth / 2;
            doc.text('Total Days Marked:', lx, mY);
            doc.text('Days Present:', lx, mY + sp);
            doc.text('Days Absent:', lx, mY + sp * 2);
            doc.text('Attendance Rate:', rx, mY);
            doc.setFont(undefined, 'bold'); doc.setTextColor(30, 41, 59);
            doc.text(`${attData.totalDays || 0}`, lx + 35, mY);
            doc.text(`${attData.daysPresent || 0}`, lx + 35, mY + sp);
            doc.text(`${attData.daysAbsent || 0}`, lx + 35, mY + sp * 2);
            doc.text(`${attData.attendanceRate || 0}%`, rx + 40, mY);
            return startY + cardHeight + 4;
        };

        const addFooterSection = (startY) => {
            const sectionWidth = (pageWidth - 4 * margin) / 3;
            const t3Summary = termsSummary.find(s => s.term === 3);
            const teacherComment = t3Summary?.comment || 'No comment';
            const adminRemark = t3Summary?.remark || 'No remark';
            doc.setFontSize(L.footer.commentFont); doc.setFont(undefined, 'normal');
            const teacherLines = doc.splitTextToSize(teacherComment, sectionWidth - 4);
            const adminLines = doc.splitTextToSize(adminRemark, sectionWidth - 4);
            const lineHeight = L.footer.commentLineH;
            const commentsRequiredHeight = 12 + 3 + teacherLines.length * lineHeight + 2 + 3 + adminLines.length * lineHeight + 4;
            const sectionHeight = Math.min(Math.max(L.footer.minH, commentsRequiredHeight), L.footer.maxH);

            const getGradeColor = (grade) => {
                const gc = { 'A': colors.success, 'A*': colors.success, 'B': colors.secondary, 'C': colors.warning, 'D': colors.warning, 'E': colors.accent, 'F': colors.accent };
                return gc[grade] || colors.text;
            };
            const getSkillColor = (rating) => {
                const sc = { 'Excellent': colors.success, 'Very Good': colors.secondary, 'Good': colors.warning, 'Fair': colors.warning, 'Poor': colors.accent };
                return sc[rating] || colors.text;
            };

            doc.setFillColor(...lightenColor(colors.primaryLight, 0.8));
            doc.roundedRect(margin, startY, sectionWidth, sectionHeight, 2, 2, 'F');
            doc.setDrawColor(...colors.primary); doc.setLineWidth(0.5);
            doc.roundedRect(margin, startY, sectionWidth, sectionHeight, 2, 2, 'S');
            doc.setFontSize(L.footer.headerFont); doc.setFont(undefined, 'bold'); doc.setTextColor(...colors.primaryDark);
            doc.text('Skills Assessment', margin + 2, startY + 6);
            doc.setFontSize(7); doc.setFont(undefined, 'normal');
            let skillY = startY + 10;
            const skills = fullResults?.skills || [];
            if (skills.length) {
                const capitalizeWords = str => str.replace(/\b\w/g, c => c.toUpperCase());
                skills.slice(0, 5).forEach(skill => {
                    const skillColor = getSkillColor(skill.rating);
                    const skillName = capitalizeWords(skill.skill || '');
                    doc.setTextColor(...colors.text);
                    doc.text(`${skillName}:`, margin + 2, skillY);
                    doc.setTextColor(...skillColor);
                    doc.text(skill.rating, margin + 2 + doc.getTextWidth(`${skillName}: `), skillY);
                    skillY += 3.5;
                });
            } else {
                doc.setTextColor(...colors.text);
                doc.text('No skills assessed', margin + 2, skillY);
            }

            let gradingX = margin + sectionWidth + margin;
            doc.setFillColor(240, 245, 255);
            doc.roundedRect(gradingX, startY, sectionWidth, sectionHeight, 2, 2, 'F');
            doc.setDrawColor(...colors.primary); doc.setLineWidth(0.5);
            doc.roundedRect(gradingX, startY, sectionWidth, sectionHeight, 2, 2, 'S');
            doc.setFontSize(L.footer.headerFont); doc.setFont(undefined, 'bold'); doc.setTextColor(...colors.primaryDark);
            doc.text('Grading System', gradingX + 2, startY + 6);
            doc.setFontSize(7); doc.setFont(undefined, 'normal');
            let gradeY = startY + 10;
            const gradeLineH = 3.5;
            gradingSystem.forEach(grade => {
                if (gradeY > startY + sectionHeight - gradeLineH) { gradeY = startY + 10; gradingX += 22; }
                doc.setTextColor(...getGradeColor(grade.grade));
                doc.text(grade.grade, gradingX + 2, gradeY);
                doc.setTextColor(...colors.text);
                doc.text(`${grade.min_score}-${grade.max_score}`, gradingX + 12, gradeY);
                gradeY += gradeLineH;
            });

            const commentsX = margin + 2 * sectionWidth + 2 * margin;
            doc.setFillColor(255, 248, 230);
            doc.roundedRect(commentsX, startY, sectionWidth, sectionHeight, 2, 2, 'F');
            doc.setDrawColor(...colors.accent); doc.setLineWidth(0.5);
            doc.roundedRect(commentsX, startY, sectionWidth, sectionHeight, 2, 2, 'S');
            doc.setFontSize(L.footer.headerFont); doc.setFont(undefined, 'bold'); doc.setTextColor(...colors.primaryDark);
            doc.text('Comments', commentsX + 2, startY + 6);
            doc.setFontSize(L.footer.commentFont);
            let cY = startY + 12;
            doc.setFont(undefined, 'bold'); doc.setTextColor(...colors.text);
            doc.text('Teacher:', commentsX + 2, cY);
            doc.setFont(undefined, 'normal'); cY += 3;
            teacherLines.forEach(line => {
                if (cY < startY + sectionHeight - 10) { doc.text(line, commentsX + 2, cY); cY += lineHeight; }
            });
            cY += 2;
            doc.setFont(undefined, 'bold');
            doc.text('Admin:', commentsX + 2, cY);
            doc.setFont(undefined, 'normal'); cY += 3;
            adminLines.forEach(line => {
                if (cY < startY + sectionHeight - 2) { doc.text(line, commentsX + 2, cY); cY += lineHeight; }
            });

            return startY + sectionHeight + 4;
        };

        const addSignature = (startY) => {
            if (signatureDataUrl) {
                try {
                    const x = pageWidth - margin - 25;
                    doc.setFillColor(...lightenColor(colors.primary, 0.9));
                    doc.roundedRect(x - 2, startY - 2, 29, 18, 2, 2, 'F');
                    doc.addImage(signatureDataUrl, 'PNG', x, startY, 25, 12);
                    doc.setDrawColor(...colors.primary); doc.setLineWidth(0.8);
                    doc.line(x, startY + 14, x + 25, startY + 14);
                    doc.setFontSize(8); doc.setTextColor(...colors.primaryDark);
                    doc.text('Principal/Head Teacher', x + 12.5, startY + 18, { align: 'center' });
                } catch {}
            }
            doc.setFontSize(7); doc.setTextColor(...colors.darkGray);
            doc.text('Powered by Acceede.com', margin, pageHeight - 2);
        };

        // Reserve the space the sections below the table occupy, so the table fits.
        const computeReservedBelow = () => {
            const attData = attendanceSummary;
            const attH = attData ? (L.attendance.h + 4) : 0;

            const sectionWidth = (pageWidth - 4 * margin) / 3;
            const t3Summary = termsSummary.find(s => s.term === 3);
            doc.setFontSize(L.footer.commentFont); doc.setFont(undefined, 'normal');
            const teacherLines = doc.splitTextToSize(t3Summary?.comment || 'No comment', sectionWidth - 4);
            const adminLines = doc.splitTextToSize(t3Summary?.remark || 'No remark', sectionWidth - 4);
            const commentsRequiredHeight = 12 + 3 + teacherLines.length * L.footer.commentLineH + 2 + 3 + adminLines.length * L.footer.commentLineH + 4;
            const footerH = Math.min(Math.max(L.footer.minH, commentsRequiredHeight), L.footer.maxH) + 4;

            const signatureH = 20;
            const safety = 4;
            return attH + footerH + signatureH + safety;
        };

        // Build the PDF as a single page
        let y = addHeader();
        y = addStudentCard(y);
        y = addTable(y, computeReservedBelow());
        y = addAttendanceSection(y);
        y = addFooterSection(y);
        addSignature(y);

        const childName = getStudentFullName(child, studentMiddlename).replace(/\s+/g, '_');
        doc.save(`${childName}_Cumulative_${session.replace('/', '-')}.pdf`);
    };

    // Create dynamic CA columns based on assessment system
    const createDynamicColumns = () => {
        const baseColumns = [
            {
                name: "Subject Name",
                selector: row => row.subject.name,
                sortable: true,
            }
        ];

        // Add dynamic CA columns for each subject
        const caColumns = [];
        const examColumns = [];
        const otherColumns = [];

        results.forEach((result, index) => {
            const assessmentSystem = getAssessmentSystemForSubject(result);
            const caSettings = assessmentSystem?.ca?.cas || [];
            const examMaxScore = assessmentSystem?.exam?.max_score || 100;

            // Create CA columns for this subject
            caSettings.forEach((ca, caIdx) => {
                const caColumnName = ca.title ? `${ca.title} (${ca.max_score})` : `CA ${caIdx + 1} (${ca.max_score})`;

                if (!caColumns.find(col => col.name === caColumnName)) {
                    caColumns.push({
                        name: caColumnName,
                        cell: (row, rowIndex) => {
                            const caData = row.cas?.find(caItem => caItem.ca === caIdx + 1);
                            return caData ? caData.score : '-';
                        },
                        sortable: true,
                    });
                }
            });

            // Create exam column
            const examColumnName = `Exam (${examMaxScore})`;
            if (!examColumns.find(col => col.name === examColumnName)) {
                examColumns.push({
                    name: examColumnName,
                    cell: (row) => row.exam || '-',
                    sortable: true,
                });
            }
        });

        // Add other columns
        otherColumns.push(
            {
                name: "Total",
                selector: row => row.total,
                sortable: true,
            },
            {
                name: "Grade",
                selector: row => calculateGrade(row.total, gradingSystem),
                sortable: true,
            },
            {
                name: "Class Average",
                selector: row => row.class_average || '-',
                sortable: true,
            },
            {
                name: "Subject Rank",
                selector: row => row.subject_rank || '-',
                sortable: true,
            },
            /*{
                name: "Status",
                cell: (row) => (
                    <div>
                        <StatusBadge status="active" label="Uploaded" />
                    </div>
                ),
                ignoreRowClick: true,
            }*/
        );

        return [...baseColumns, ...caColumns, ...examColumns, ...otherColumns];
    };

    const columns = createDynamicColumns();

    return (
        <div className="w-full flex items-center justify-center py-14 md:py-8 relative">
            <div className="bg-transparent w-full pb-16 px-3 md:px-9 py-8 sm:py-0">
                <div className="bg-white rounded-2xl p-6">
                    <BackButton history={history} />
                    <div className="w-14 h-14 rounded-full bg-blue-500 mb-4 mt-4 border-[3px] border-[#D8846E] overflow-hidden">
                        {student?.picture?.path ? (
                            <img
                                src={student.picture.path}
                                alt="Student"
                                className="w-full h-full rounded-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        ) : (
                            <div className="w-full h-full rounded-full bg-blue-500 flex items-center justify-center">
                                <FiUser className="w-6 h-6 text-white" />
                            </div>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center justify-between">
                        <div className="w-full sm:w-max mb-4 sm:mb-0">
                            <p className="text-2xl font-semibold mb-2">{student?.firstname} {student?.lastname}</p>
                            <p className="text-sm text-[#606060] font-medium">
                                {selectedTerm.name} - {selectedSession.name}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="mt-10 p-5 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg">
                    {/* Header Section - Mobile Responsive */}
                    <div className="space-y-4 mb-6">
                        {/* Title */}
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <h1 className="text-xl md:text-2xl font-bold text-slate-800">
                                Student Result
                            </h1>
                            {fullResults?.overall_rank && (
                                <span className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                                    Computed Position: {fullResults.overall_rank}
                                </span>
                            )}
                        </div>

                        {/* Filters Section - Mobile Responsive */}
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 dropdown-container">
                            {/* Term Filter */}
                            <div className="relative flex-1 sm:flex-none dropdown-container">
                                <button
                                    type='button'
                                    onClick={handleTermDropdown}
                                    className='w-full sm:w-auto text-sm font-semibold flex items-center justify-between bg-white hover:bg-indigo-50 border-2 border-indigo-200 hover:border-indigo-300 py-2.5 px-4 rounded-xl transition-all duration-200'
                                >
                                    <span className='flex items-center gap-2'>
                                        <span className="text-slate-600 font-medium">Term:</span>
                                        <span className="text-indigo-700">{selectedTerm.name}</span>
                                    </span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`ml-2 transition-transform duration-200 ${termDropdown ? 'rotate-180' : ''}`}>
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                        <path d="M6 9l6 6l6 -6" />
                                    </svg>
                                </button>
                                {termDropdown && (
                                    <div className="absolute left-0 right-0 sm:left-auto sm:right-0 bg-white border-2 border-indigo-200 rounded-xl shadow-xl mt-2 z-20 max-h-60 overflow-y-auto min-w-full sm:min-w-40">
                                        {[1, 2, 3].map(termNum => (
                                            <div
                                                key={termNum}
                                                onClick={() => handleTermSelection(termNum.toString(), `Term ${termNum}`)}
                                                className="cursor-pointer text-sm px-4 py-3 hover:bg-indigo-50 hover:font-semibold transition-colors border-b border-slate-100 last:border-b-0"
                                            >
                                                Term {termNum}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Session Filter */}
                            <div className="relative flex-1 sm:flex-none dropdown-container">
                                <button
                                    type='button'
                                    onClick={handleSessionDropdown}
                                    className='w-full sm:w-auto text-sm font-semibold flex items-center justify-between bg-white hover:bg-indigo-50 border-2 border-indigo-200 hover:border-indigo-300 py-2.5 px-4 rounded-xl transition-all duration-200'
                                >
                                    <span className='flex items-center gap-2'>
                                        <span className="text-slate-600 font-medium">Session:</span>
                                        <span className="text-indigo-700 truncate">{selectedSession.name}</span>
                                    </span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`ml-2 flex-shrink-0 transition-transform duration-200 ${sessionDropdown ? 'rotate-180' : ''}`}>
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                        <path d="M6 9l6 6l6 -6" />
                                    </svg>
                                </button>
                                {sessionDropdown && (
                                    <div className="absolute left-0 right-0 sm:left-auto sm:right-0 bg-white border-2 border-indigo-200 rounded-xl shadow-xl mt-2 z-20 max-h-60 overflow-y-auto min-w-full sm:min-w-48">
                                        {schoolSessions.map(session => (
                                            <div
                                                key={session.value}
                                                onClick={() => handleSessionSelection(session.value, session.label)}
                                                className="cursor-pointer text-sm px-4 py-3 hover:bg-indigo-50 hover:font-semibold transition-colors border-b border-slate-100 last:border-b-0"
                                            >
                                                {session.label}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Action Buttons Section - Mobile Responsive */}
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
                            {/* Primary Actions - Downloads */}
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 flex-1">
                                <button
                                    type="button"
                                    onClick={openScoreEntry}
                                    className="group flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white px-4 sm:px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-200 shadow-md hover:shadow-lg"
                                    title="Enter scores for the subjects you teach"
                                >
                                    <FiEdit3 className="w-4 h-4" />
                                    <span className="whitespace-nowrap">Enter Results</span>
                                </button>
                                <button
                                    onClick={exportMidTermPDF}
                                    className="group flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 active:scale-95 text-white px-4 sm:px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                                    title="Download Mid-Term Test Results"
                                >
                                    <FiFileText className="w-4 h-4 group-hover:scale-110 transition-transform flex-shrink-0" />
                                    <span className="whitespace-nowrap">Mid-Term Results</span>
                                </button>
                                <button
                                    onClick={exportPDF}
                                    className="group flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-800 to-indigo-900 hover:from-indigo-900 hover:to-indigo-950 active:scale-95 text-white px-4 sm:px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                                    title="Download Full Academic Report"
                                >
                                    <FiDownload className="w-4 h-4 group-hover:scale-110 transition-transform flex-shrink-0" />
                                    <span className="whitespace-nowrap">Full Result</span>
                                </button>
                                <button
                                    onClick={exportCumulativePDF}
                                    className="group flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 active:scale-95 text-white px-4 sm:px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                                    title="Download Cumulative Academic Report"
                                >
                                    <FiDownload className="w-4 h-4 group-hover:scale-110 transition-transform flex-shrink-0" />
                                    <span className="whitespace-nowrap">Cumulative Result</span>
                                </button>
                            </div>

                            {/* Secondary Action - Review */}
                            <button
                                type="button"
                                onClick={toggleCommentModal}
                                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-indigo-50 hover:bg-indigo-100 active:scale-95 text-indigo-700 hover:text-indigo-800 border-2 border-indigo-200 hover:border-indigo-300 px-4 sm:px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-200 whitespace-nowrap"
                                title="Add general review and comments"
                            >
                                <FiMessageSquare className="w-4 h-4 flex-shrink-0" />
                                <span>Add Review</span>
                            </button>
                        </div>
                    </div>

                    <div className="border rounded-3xl">
                        <DataTable
                            data={results}
                            columns={columns}
                            getRowId={(row) => row._id}
                            pagination
                            paginationComponent={CustomPagination}
                            customStyles={customStyles}
                            progressPending={loading}
                            progressComponent={<SkeletonTable rows={5} columns={columns.length} />}
                            highlightOnHover
                            striped
                            onRowClicked={(row) => {
                                history.push({
                                    pathname: `/t/results/upload/${studentId}`, state: { student: student }
                                });
                            }}
                            pointerOnHover
                            noDataComponent={
                                <div className="py-10 text-center">
                                    <p className="text-sm font-medium text-slate-700">No results recorded for this term yet.</p>
                                    <p className="mt-1 text-xs text-slate-500">Enter scores for the subjects assigned to you.</p>
                                    <button type="button" onClick={openScoreEntry} className="mt-4 rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700">Enter Results</button>
                                </div>
                            }
                        />
                    </div>
                </div>
            </div>
            {isModalOpen &&
                <EditResultDetailsModal
                    closeModal={toggleResultModal}
                    onResultEdited={fetchResults}
                    childId={student._id}
                    teacherId={user._id}
                    school={school}
                    classId={classId}
                    subject={selectedSubject}
                />
            }
            {isCommentModalOpen &&
                <AddCommentModal
                    closeModal={toggleCommentModal}
                    onCommentAdded={fetchResults}
                    childId={student._id}
                    teacherId={user._id}
                    school={school}
                    classId={classId}
                    results={fullResults}
                    term={selectedTerm.id || schoolTerm}
                    session={selectedSession.id || schoolSession}
                />
            }
        </div>
    );
};

export default ViewStudentResult;
